import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import Select from "components/common/Select";

import "./MoveToGroupDialog.scss";

const HELPER_COPY =
  "Moving a user to a group sets what data they can see. The account owner must stay in the Main group.";

// Presentational dialog used to move/reassign a single customer user between groups (including
// back to Main). The actual assignMembers call + list refresh + snackbar are handled by the
// parent's onSubmit so this stays easy to test and reuse. groupOptions already include the Main
// group (labelled "(Main)") so moving a user back to Main is a first-class target.
function MoveToGroupDialog({ open, user, groupOptions, isSubmitting, onClose, onSubmit }) {
  const [targetGroupId, setTargetGroupId] = useState("");

  // Pre-select the user's current group so a no-op submit is impossible without changing it.
  useEffect(() => {
    if (open) setTargetGroupId(user?.groupId || "");
  }, [open, user?.groupId]);

  const userName = useMemo(
    () => `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "this user",
    [user]
  );

  if (!open) return null;

  const isUnchanged = !targetGroupId || targetGroupId === user?.groupId;

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={<DialogTitle title={`Move "${userName}" to group`} />}
      content={
        <DialogContent>
          <div className="move-to-group-dialog">
            <Select
              name="targetGroup"
              label="Group"
              value={targetGroupId}
              onChange={(val) => setTargetGroupId(val)}
              options={groupOptions}
              placeholder="Select group"
            />
            <p className="para-body-s-regular move-to-group-dialog__helper">{HELPER_COPY}</p>
          </div>
        </DialogContent>
      }
      actions={
        <DialogActions>
          <CustomButton label="Cancel" variant="text" onClick={onClose} />
          <CustomButton
            label="Move"
            variant="contained"
            onClick={() => onSubmit(targetGroupId)}
            isDisabled={isUnchanged || isSubmitting}
            isLoading={isSubmitting}
          />
        </DialogActions>
      }
    />
  );
}

MoveToGroupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  groupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

MoveToGroupDialog.defaultProps = {
  user: null,
  groupOptions: [],
  isSubmitting: false,
};

export default MoveToGroupDialog;
